import {
  GoAFormItem,
  GoARadioGroup,
  GoARadioItem,
  GoASpacer,
} from '@abgov/react-components';
import React, { useEffect, useState } from 'react';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../state/store';
import {
  defaultValidate,
  WizardButtonGroup,
} from '../wizard/wizard-button-group-component';
import { removeAllValidationErrorNotifications } from '../../state/slices/notificationSlice';
import { updateCollision } from '../../state/slices/collisionSlice';
import { Collision } from '../../models/collision';
import {
  getReturnToValue,
  IsInReviewEditMode,
} from '../wizard/wizard-component';

interface SpillLoadComponentProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const SpillLoadComponent = ({
  navigationConfiguration,
}: SpillLoadComponentProps) => {
  const dispatch = useAppDispatch();

  const [formError, setFormError] = useState('');
  const [selection, setSelection] = useState('');

  const [nextPage, setNextPage] = useState('');

  const defaultSelection = '0';
  const errorText = 'Answer yes or no in order to continue';

  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  const stateCollision = useAppSelector((state: RootState) => state.collision);

  useEffect(() => {
    setSelection(
      stateCollision.collision.vehicles![0].damage?.loadSpillage
        ? stateCollision.collision.vehicles![0].damage.loadSpillage!.toString()
        : defaultSelection,
    );
  }, [stateCollision.collision.vehicles![0].damage?.loadSpillage]);

  useEffect(() => {
    let pageConfig = 'error';

    pageConfig = navigationConfiguration.pagingConfiguration.find(
      (item) => item.default !== undefined,
    )!.default;

    setNextPage(pageConfig);
  }, [selection]);

  const save = () => {
    if (selection) {
      let updatedCollision: Collision = { ...stateCollision.collision };

      if (
        navigationConfiguration.updateMetadata &&
        navigationConfiguration.stepId
      ) {
        const stepUpdated = navigationConfiguration.updateMetadata(
          updatedCollision,
          navigationConfiguration.stepId!,
          false,
          true,
        );

        if (stepUpdated && updatedCollision.metadata) {
          updatedCollision.metadata = {
            ...updatedCollision.metadata,
            formState: stepUpdated,
          };
        }
      }
      updatedCollision.vehicles = [
        {
          ...stateCollision.collision.vehicles?.[0],
          damage: {
            ...stateCollision.collision.vehicles?.[0].damage,
            loadSpillage: parseInt(selection),
          },
        },
      ];

      dispatch(updateCollision(updatedCollision));
    }
    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  return (
    <div>
      <h1>Vehicle damage</h1>
      The collision details section is the starting point for drivers to
      identify the type of collision they were involved in. In some cases you’ll
      be prompted to add some additional details depending on your type of
      collision.
      <h2>Did you lose and/or spill any items from your load?</h2>
      <GoASpacer vSpacing="m" />
      <GoAFormItem error={formError}>
        <GoARadioGroup
          error={formError.length > 0}
          name="attachmentRadioGroup"
          value={selection}
          onChange={(selection, id) => {
            setSelection(id);
            setFormError('');
            dispatch(removeAllValidationErrorNotifications());
          }}
        >
          <GoARadioItem
            value={stateConfiguration.configuration.loadSpillage
              .find((x) => {
                return x.value.toLowerCase() == 'yes';
              })
              ?.id.toString()}
            label="Yes"
          ></GoARadioItem>
          <GoARadioItem
            value={stateConfiguration.configuration.loadSpillage
              .find((x) => {
                return x.value.toLowerCase() == 'no';
              })
              ?.id.toString()}
            label="No"
          ></GoARadioItem>
        </GoARadioGroup>
      </GoAFormItem>
      <WizardButtonGroup
        nextPage={nextPage}
        validate={(): boolean => {
          return defaultValidate(
            selection,
            defaultSelection,
            errorText,
            setFormError,
            navigationConfiguration.errorNotification,
          );
        }}
        goToReview={navigationConfiguration.goToReview}
        showViewSummaryButton={!IsInReviewEditMode()}
        save={save}
        navigate={navigationConfiguration.navigate}
      />
    </div>
  );
};
